





































































































































































import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import posed, { PoseTransition } from 'vue-pose';

import { Picture } from '@/components/atoms';
import PhoneInputCountryItem from '@/components/molecules/PhoneInputCountryItem/PhoneInputCountryItem.vue';
import { TextField } from '@/components/atoms';
import SearchIcon from '@/components/icons/Search.vue';
import CloseIcon from '@/components/icons/CloseModal.vue';
import InfoIcon from '@/components/icons/InfoV2.vue';

import { treatError } from '@/utils';
import { getCountryFlagFile } from '@/constants/countryFlags';
import {
  getPhoneMask,
  getPhoneMaskPlaceholder,
  PhoneMask
} from '@/constants/phoneMasks';

import InternationalizationService from '@/api/InternationalizationService';

import { IInternationalization } from '@/models/internationalization';

let timer: NodeJS.Timeout;
const DELAY = 500;

type Option = IInternationalization;

export type PhoneInputVariant = 'normal' | 'new-field';

const defaultValue = () => ({
  code: '',
  iso: '',
  number: ''
});

@Component<PhoneInput>({
  components: {
    PoseTransition,
    Shade: posed.div({
      enter: {
        opacity: 1,
        transition: { duration: 200 }
      },
      exit: {
        opacity: 0,
        transition: { duration: 200 }
      }
    }),
    TextField,
    Picture,
    PhoneInputCountryItem,
    SearchIcon,
    CloseIcon,
    InfoIcon
  },
  computed: {
    optionalAfterVar() {
      return {
        '--optional-value': `" (${this.$t(`${this.tPrefix}.optional`)})"`
      };
    }
  }
})
export default class PhoneInput extends Vue {
  @Prop(String) readonly label!: string;
  @Prop({ default: 'phone_input' }) instanceId!: string;
  @Prop({ default: false }) readonly optional?: boolean;
  @Prop({ default: 'normal' }) readonly variant?: PhoneInputVariant;
  @Prop({ default: false }) readonly hasError?: boolean;
  @Prop({ default: false }) readonly fixedPosition?: boolean;
  @Prop({ default: '0px' }) readonly tooltipMinWidth?: string;
  @Prop({ default: defaultValue })
  value?: {
    code: string;
    number: string;
    iso: string;
  };
  @Prop({ default: '' }) helpText?: string;
  @Prop({ default: true }) showHelpIcon?: boolean;
  @Prop({ default: true }) showCountry?: boolean;
  @Prop() defaultCountry?: any;

  internationalizationService: InternationalizationService = new InternationalizationService();

  filterInput = '';
  countries: IInternationalization[] = [];
  filteredCountries: IInternationalization[] = [];

  selectedCountry = {} as Option;

  selectOpened: boolean = false;

  get initialPhoneCountryIso() {
    if (this.value && this.value.iso) return this.value.iso;

    return 'US';
  }

  /**
   * A getter, never a data field: `vue-the-mask` sorts a mask list in place on
   * every directive update, and on an observed array that sort notifies the
   * render, which runs the directive again — an endless input loop that froze
   * the form's Save button (TWISM-21037).
   *
   * Empty until the country resolves: the directive skips a falsy mask, so a
   * stored number is not cut to the default length before its own mask lands.
   */
  get phoneMask(): PhoneMask {
    if (!this.selectedCountry || !this.selectedCountry.countryIso2) return '';

    return getPhoneMask(this.selectedCountry);
  }

  get phoneMaskPlaceholder() {
    return getPhoneMaskPlaceholder(this.phoneMask);
  }

  get tPrefix() {
    return 'common.components.phoneInput';
  }

  get isMobileDevice() {
    return this.$mobileCheck();
  }

  get selectedCountryFlagFile() {
    return getCountryFlagFile(
      this.selectedCountry && this.selectedCountry.countryIso2
    );
  }

  get hasSelectedCountry() {
    return !!this.selectedCountryFlagFile;
  }

  get shouldShowTooltip() {
    return !this.$mobileCheck() && this.fixedPosition;
  }

  created() {
    this.getCountries();
  }

  beforeDestroy() {
    this.setContainerOverflowY();
  }

  handleOutsideClick() {
    if (!this.selectOpened) return;

    this.selectOpened = false;
    this.setContainerOverflowY();
  }

  toggleSelect() {
    const newSelectOpenedValue = !this.selectOpened;

    this.selectOpened = newSelectOpenedValue;

    if (newSelectOpenedValue) {
      this.setContainerOverflowY('hidden');

      return;
    }

    this.setContainerOverflowY();
  }

  onInput(phoneNumber: string) {
    if (this.value)
      this.value.number = phoneNumber ? phoneNumber.replace(/[^\d]+/g, '') : '';
    this.$emit('input', this.value);
  }

  async getCountries() {
    this.$showLoaderWithLocker();

    try {
      const sessionCountries = this.$store.getters[
        'internationalization/getCountries'
      ];

      this.countries = sessionCountries;
      this.filteredCountries = sessionCountries;

      this.initSelectState();
    } catch (e) {
      treatError(e);
    } finally {
      this.$hideLoader();
    }
  }

  initSelectState() {
    const country = this.countries.find(
      country => country.countryIso2 === this.initialPhoneCountryIso
    );

    if (country) this.handleCountryClick(country);
  }

  handleCountryClick(country: IInternationalization) {
    this.selectedCountry = country;
    this.closeSelect();

    if (this.shouldShowTooltip) {
      this.handleCloseTooltip();
    }
  }

  @Watch('value', { deep: true })
  onValueChanged(newValue: any, oldValue: any) {
    if (
      newValue &&
      oldValue &&
      newValue.iso !== oldValue.iso &&
      this.countries.length > 0
    ) {
      const country = this.countries.find(
        country => country.countryIso2 === newValue.iso
      );
      if (country && country !== this.selectedCountry) {
        this.selectedCountry = country;
      }
    }
  }

  @Watch('defaultCountry')
  onDefaultCountryChanged() {
    this.handleCountryClick(this.defaultCountry);
  }

  @Watch('selectedCountry')
  onCountryChanged() {
    this.selectOpened = false;
    if (this.value) {
      this.value.code = this.selectedCountry.phoneCode;
      this.value.iso = this.selectedCountry.countryIso2;
      this.$emit('input', this.value);
    }
    this.clearSearch();
  }

  handleSearch(event?: any) {
    clearTimeout(timer);

    if (!this.filterInput) {
      this.filteredCountries = this.countries;

      return;
    }

    timer = setTimeout(() => {
      const filteredCountries = this.countries.filter(country => {
        const countryName = country.name.toLowerCase();

        return countryName.includes(this.filterInput.toLowerCase());
      });

      this.filteredCountries = filteredCountries;
    }, DELAY);
  }

  setContainerOverflowY(overflowY = 'auto') {
    const el = document.querySelector<HTMLElement>('.container-scrollable');
    if (this.isMobileDevice && el) {
      el.style.overflowY = overflowY;
    }
  }

  closeSelect() {
    this.selectOpened = false;

    this.setContainerOverflowY();
    this.clearSearch();
  }

  clearSearch() {
    this.filterInput = '';
    this.handleSearch();
  }

  onlyNumber(event: KeyboardEvent) {
    if (isNaN(Number(event.key))) {
      event.preventDefault();
    }
  }

  handleCloseTooltip() {
    const triggerEl = document.querySelector(
      '[data-tippy="phone-input-tippy"]'
    );

    if (triggerEl) {
      triggerEl.dispatchEvent(new Event('click'));
    }
  }
}
